import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useStore } from 'reactflow';
import { Plus, MessageSquare, ImagePlus, Music, Video, FileText } from 'lucide-react';

export interface WorkflowActions {
  addConnectedNode: (sourceNodeId: string, nodeType: string) => void;
}

export const WorkflowActionsContext = createContext<WorkflowActions | null>(null);

const nodeOptions = [
  { type: 'textMessage', icon: MessageSquare, label: 'Texto', color: 'text-primary hover:bg-green-50' },
  { type: 'imageMessage', icon: ImagePlus, label: 'Imagen', color: 'text-secondary hover:bg-blue-50' },
  { type: 'audioMessage', icon: Music, label: 'Audio', color: 'text-purple-500 hover:bg-purple-50' },
  { type: 'videoMessage', icon: Video, label: 'Video', color: 'text-orange-500 hover:bg-orange-50' },
  { type: 'documentMessage', icon: FileText, label: 'Documento', color: 'text-indigo-500 hover:bg-indigo-50' },
];

interface AddNodeMenuProps {
  nodeId: string;
}

export function AddNodeMenu({ nodeId }: AddNodeMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const actions = useContext(WorkflowActionsContext);

  // Reactively check if this node has an outgoing edge
  const hasOutgoing = useStore((s) => s.edges.some((e) => e.source === nodeId));

  // Close menu on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Don't show if node has outgoing connection or no context
  if (hasOutgoing || !actions) return null;

  const handleSelect = (type: string) => {
    actions.addConnectedNode(nodeId, type);
    setIsOpen(false);
  };

  return (
    <div
      ref={menuRef}
      className="absolute top-1/2 -translate-y-1/2 z-20 nodrag nopan"
      style={{ right: '-40px' }}
    >
      {!isOpen ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="w-7 h-7 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center shadow-md transition-all hover:scale-110 border-2 border-white"
          title="Agregar nodo siguiente"
        >
          <Plus className="w-4 h-4" />
        </button>
      ) : (
        <div className="flex flex-col gap-1 bg-white rounded-xl shadow-2xl border border-gray-200 p-1.5">
          {nodeOptions.map(({ type, icon: Icon, label, color }) => (
            <button
              key={type}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(type);
              }}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${color}`}
              title={label}
            >
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
